import React, { useState, useRef } from 'react';
import { addCatalogueLead } from '../../firebase/catalogueLeadsService';
import { auth } from '../../firebase/firebaseConfig';
import { Timestamp } from 'firebase/firestore';
import './CatalogueLeadsCSVImport.css';

/**
 * CatalogueLeadsCSVImport Component
 * Allows bulk import of historical catalogue leads via CSV file upload
 */
const CatalogueLeadsCSVImport = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [step, setStep] = useState('upload'); // upload, validate, import, complete
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      parseCSV(selectedFile);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  // Parse CSV file
  const parseCSV = (file) => {
    setProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        
        // Split into lines
        const lines = text.split(/\r?\n/).filter(line => line.trim());
        
        if (lines.length < 2) {
          throw new Error('CSV file must contain at least a header row and one data row');
        }

        // Parse header
        const headers = parseCSVLine(lines[0], null).map(h => h.trim());
        const requiredHeaders = ['name', 'contactNumber', 'email', 'companyName', 'dateOfEnquiry'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
        }

        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i];
          if (!line.trim()) continue;
          
          const row = parseCSVLine(line, headers);
          row.lineNumber = i + 1;
          data.push(row);
        }

        if (data.length === 0) {
          throw new Error('CSV file contains no data rows');
        }

        setCsvData(data);
        setStep('validate');
        validateData(data);
      } catch (error) {
        alert(`Error parsing CSV: ${error.message}`);
        setFile(null);
      } finally {
        setProcessing(false);
      }
    };

    reader.readAsText(file);
  };

  // Parse CSV line handling quoted fields with commas
  const parseCSVLine = (line, headers) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    
    if (!headers) {
      return values.map(v => v.trim());
    }
    
    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    
    return row;
  };

  // Parse date from DD-MM-YYYY HH:mm:ss format
  const parseDateToTimestamp = (dateString) => {
    if (!dateString || dateString.trim() === '') {
      return Timestamp.now();
    }

    try {
      // Format: "04-05-2026 20:22:57" or "28-08-2025"
      const parts = dateString.trim().split(' ');
      const datePart = parts[0];
      const timePart = parts[1] || '00:00:00';

      const [day, month, year] = datePart.split('-');
      const [hour, minute, second] = timePart.split(':');

      const date = new Date(
        parseInt(year),
        parseInt(month) - 1, // Month is 0-indexed
        parseInt(day),
        parseInt(hour) || 0,
        parseInt(minute) || 0,
        parseInt(second) || 0
      );

      return Timestamp.fromDate(date);
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return Timestamp.now();
    }
  };

  // Validate CSV data
  const validateData = (data) => {
    const results = data.map((row, index) => {
      const errors = [];
      
      // Validate required fields
      if (!row.name || row.name.trim() === '') {
        errors.push('Name is required');
      }
      
      if (!row.contactNumber || row.contactNumber.trim() === '') {
        errors.push('Contact number is required');
      }
      
      // Email is optional, but validate format if provided
      if (row.email && row.email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(row.email)) {
          errors.push('Invalid email format');
        }
      }
      
      // Validate date format
      if (row.dateOfEnquiry) {
        const dateRegex = /^\d{2}-\d{2}-\d{4}(\s+\d{2}:\d{2}:\d{2})?$/;
        if (!dateRegex.test(row.dateOfEnquiry.trim())) {
          errors.push('Date must be in DD-MM-YYYY or DD-MM-YYYY HH:mm:ss format');
        }
      }
      
      return {
        lineNumber: row.lineNumber,
        data: row,
        valid: errors.length === 0,
        errors: errors
      };
    });
    
    setValidationResults(results);
  };

  // Import leads to Firestore
  const importLeads = async () => {
    setImporting(true);
    setStep('import');
    
    const validLeads = validationResults.filter(r => r.valid);
    const results = {
      total: validLeads.length,
      success: 0,
      failed: 0,
      errors: []
    };
    
    try {
      const currentUser = auth.currentUser;
      
      for (let i = 0; i < validLeads.length; i++) {
        const row = validLeads[i].data;
        
        try {
          const createdAtTimestamp = parseDateToTimestamp(row.dateOfEnquiry);
          
          const leadData = {
            name: row.name || '',
            contactNumber: row.contactNumber || '',
            email: row.email || '',
            companyName: row.companyName || 'NA',
            dateOfEnquiry: row.dateOfEnquiry || '',
            status: 'new',
            contactedDate: null,
            followUpDate: null,
            notes: '',
            timestamp: row.dateOfEnquiry || new Date().toLocaleString('en-SG'),
            source: 'import',
            emailSent: true, // Historical records - email was already sent
            createdAt: createdAtTimestamp,
          };
          
          await addCatalogueLead(leadData);
          results.success++;
        } catch (error) {
          console.error(`Error importing lead ${row.name}:`, error);
          results.failed++;
          results.errors.push({
            line: row.lineNumber,
            name: row.name,
            error: error.message
          });
        }
        
        // Update progress
        setProgress(Math.round(((i + 1) / validLeads.length) * 100));
        
        // Small delay to avoid overwhelming Firestore
        if ((i + 1) % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      setResults(results);
      setStep('complete');
      
      if (results.success > 0 && onSuccess) {
        onSuccess(results);
      }
    } catch (error) {
      alert(`Import error: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  // Reset to start over
  const handleReset = () => {
    setFile(null);
    setCsvData([]);
    setValidationResults([]);
    setResults(null);
    setStep('upload');
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Render functions for each step
  const renderUploadStep = () => (
    <div className="csv-import-step">
      <h3>📥 Upload Catalogue Leads CSV</h3>
      <p>Select a CSV file containing catalogue lead data to import.</p>
      
      <div className="csv-format-info">
        <h4>Required CSV Format:</h4>
        <pre>name,contactNumber,email,companyName,dateOfEnquiry</pre>
        <ul>
          <li><strong>name:</strong> Lead's name (required)</li>
          <li><strong>contactNumber:</strong> Phone number (required)</li>
          <li><strong>email:</strong> Email address (optional)</li>
          <li><strong>companyName:</strong> Company name (use "NA" if unknown)</li>
          <li><strong>dateOfEnquiry:</strong> Date in DD-MM-YYYY or DD-MM-YYYY HH:mm:ss format</li>
        </ul>
      </div>
      
      <div className="file-upload-section">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="file-input"
        />
        {file && (
          <div className="file-selected">
            ✓ Selected: {file.name}
          </div>
        )}
      </div>
      
      {processing && (
        <div className="processing-message">
          <div className="spinner"></div>
          <p>Processing CSV file...</p>
        </div>
      )}
    </div>
  );

  const renderValidateStep = () => {
    const validCount = validationResults.filter(r => r.valid).length;
    const invalidCount = validationResults.filter(r => !r.valid).length;
    
    return (
      <div className="csv-import-step">
        <h3>✅ Validation Results</h3>
        <div className="validation-summary">
          <div className="summary-stat valid">
            <span className="stat-label">Valid:</span>
            <span className="stat-value">{validCount}</span>
          </div>
          <div className="summary-stat invalid">
            <span className="stat-label">Invalid:</span>
            <span className="stat-value">{invalidCount}</span>
          </div>
          <div className="summary-stat total">
            <span className="stat-label">Total:</span>
            <span className="stat-value">{validationResults.length}</span>
          </div>
        </div>
        
        {invalidCount > 0 && (
          <div className="validation-errors">
            <h4>❌ Invalid Records:</h4>
            <div className="error-list">
              {validationResults
                .filter(r => !r.valid)
                .map((result, idx) => (
                  <div key={idx} className="error-item">
                    <strong>Line {result.lineNumber}:</strong> {result.data.name}
                    <ul>
                      {result.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </div>
        )}
        
        <div className="action-buttons">
          {validCount > 0 && (
            <button onClick={importLeads} className="btn-primary">
              Import {validCount} Valid Lead{validCount !== 1 ? 's' : ''}
            </button>
          )}
          <button onClick={handleReset} className="btn-secondary">
            Start Over
          </button>
        </div>
      </div>
    );
  };

  const renderImportStep = () => (
    <div className="csv-import-step">
      <h3>⏳ Importing Leads...</h3>
      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="progress-text">{progress}%</div>
      </div>
      <p>Please wait while we import your catalogue leads...</p>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="csv-import-step">
      <h3>🎉 Import Complete!</h3>
      <div className="import-results">
        <div className="result-stat success">
          <span className="stat-label">Successfully Imported:</span>
          <span className="stat-value">{results.success}</span>
        </div>
        {results.failed > 0 && (
          <div className="result-stat failed">
            <span className="stat-label">Failed:</span>
            <span className="stat-value">{results.failed}</span>
          </div>
        )}
      </div>
      
      {results.errors.length > 0 && (
        <div className="import-errors">
          <h4>⚠️ Import Errors:</h4>
          <div className="error-list">
            {results.errors.map((error, idx) => (
              <div key={idx} className="error-item">
                <strong>Line {error.line}:</strong> {error.name}
                <p>{error.error}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="action-buttons">
        <button onClick={onClose} className="btn-primary">
          View Leads
        </button>
        <button onClick={handleReset} className="btn-secondary">
          Import More
        </button>
      </div>
    </div>
  );

  return (
    <div className="csv-import-overlay">
      <div className="csv-import-modal">
        <div className="csv-import-header">
          <h2>Import Catalogue Leads from CSV</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        
        <div className="csv-import-content">
          {step === 'upload' && renderUploadStep()}
          {step === 'validate' && renderValidateStep()}
          {step === 'import' && renderImportStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>
      </div>
    </div>
  );
};

export default CatalogueLeadsCSVImport;
